import { Component, Input, Output, OnInit, EventEmitter, OnChanges, SimpleChanges, OnDestroy, AfterViewInit, ElementRef } from '@angular/core';
import { AppService } from 'app/app.service';
import { BaseComponent } from 'app/shared/components/base/base.component';
import { get } from 'lodash';
import { Subscription } from 'rxjs';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
export interface KuiTabBarTitleID {
    title: string;
    id: string;
    icon?: string;
    disabled?: boolean;
}

export interface KuiTabBarDragEvent {
    result: string[];
    id: string;
}

@Component({
    selector: 'kui-tab-bar',
    templateUrl: './tab-bar.component.html',
    standalone: false
})

export class KuiTabBarComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    private dropSubscription: Subscription;
    private resizeObserver: ResizeObserver | null = null;
    _items: KuiTabBarTitleID[];

    @Input() items: Array<string | KuiTabBarTitleID>;
    @Input() mobile: boolean;
    @Input() selected: string | number;
    @Input() stretchItems: boolean;
    @Input() closeButton: boolean;
    @Input() draggable: boolean;

    @Output() selection = new EventEmitter<string>();
    @Output() reorder = new EventEmitter<KuiTabBarDragEvent>();
    @Output() close = new EventEmitter<string>();

    indicatorStyle = 'indicator-none';

    constructor(app: AppService, private elementRef: ElementRef<HTMLElement>) {
        super();

        this.on(app.theme$, theme => {
            this.indicatorStyle = 'indicator-' + get(theme.settings, 'tab.active.indicator.style', 'none');
        });
    }

    ngOnInit() {
        // Avoid ExpressionChangedAfterItHasBeenCheckedError by wrapping it in setTimeout
        setTimeout(() => {
            if (!this.selected && this.items.length > 0) {
                this.emitSelection(this._items[0]);
            }
        });

    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.items) {
            this._items = (this.items || []).map((item) => {
                if (typeof item === 'string') {
                    return { title: item, id: item };
                }
                return item;
            });
            requestAnimationFrame(() => this.evaluateTabCollapse());
        }
    }

    ngAfterViewInit() {
        // Observe the header container, not the host: the host shrink-wraps its content
        // (width: auto), so once tabs collapse it stops changing size and would never
        // fire again on grow. The container tracks the actual available width.
        const host = this.elementRef.nativeElement;
        const target = host.closest('.content-header') ?? host;
        this.resizeObserver = new ResizeObserver(() => this.evaluateTabCollapse());
        this.resizeObserver.observe(target);
    }

    ngOnDestroy() {
        if (this.draggable && this.dropSubscription) {
            this.dropSubscription.unsubscribe();
        }
        this.resizeObserver?.disconnect();
    }

    // Collapse tabs to icon-only, right-to-left, while any tab's label is truncated.
    // Only tabs that declare an icon can collapse; the rest keep the default truncate.
    private evaluateTabCollapse(): void {
        const listItems = Array.from(this.elementRef.nativeElement.querySelectorAll<HTMLElement>('.tab-bar li'));
        if (listItems.length === 0) return;

        listItems.forEach(li => li.classList.remove('collapsed'));

        const isTruncated = () => listItems.some(li => {
            const label = li.querySelector<HTMLElement>('span.text-truncate');
            return label && label.scrollWidth > label.clientWidth;
        });

        for (let i = listItems.length - 1; i >= 0 && isTruncated(); i--) {
            if (this._items[i]?.icon) {
                listItems[i].classList.add('collapsed');
            }
        }
    }

    emitSelection(item: KuiTabBarTitleID) {
        if (!item.disabled) {
            this.selected = item.id;
            this.selection.emit(item.id);
        }
    }

    emitClose(item: KuiTabBarTitleID, e: Event) {
        e.stopPropagation();
        this.close.emit(item.id);
    }

    isSelected(item: KuiTabBarTitleID): boolean {
        return this.selected === item.id;
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this._items, event.previousIndex, event.currentIndex);
        this.reorder.emit({
            id: this._items[event.previousIndex].id,
            result: Array.from(this._items).map(item => item.id),
        });
    }
}
